import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';
import { makeCancelable } from 'ui-components/src/utilities/makeCancelable';
import { useLogger } from 'ui-components/src/hooks/useLogger';
import { ConfigContext } from '../ConfigContext';

const AuthTokenContext = createContext<AuthTokenContextValue | null>(null);

export const useAuthTokenContext = () => {
  const context = useContext(AuthTokenContext);
  if (!context) {
    throw new Error(
      'useAuthTokenContext can only be used inside AuthTokenProvider',
    );
  }
  return context;
};

// Fixed: Replaced {} with unknown to avoid the empty-object-type error
export const AuthContext: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const logger = useLogger();
  const config = ConfigContext.useContainer();
  const [token, setToken] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [amplifyConfigured, setAmplifyConfigured] = useState(false);
  const tokenRef = useRef(token);

  /** --------------------------------------------------
   * Configure Amplify from dynamic config.json once loaded
   * -------------------------------------------------- */
  useEffect(() => {
    if (!config?.cognito) return;

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: config.cognito.userPoolId,
          userPoolClientId: config.cognito.clientId,
          loginWith: {
            oauth: {
              // Strip https:// if present — Amplify v6 needs domain only
              domain: config.cognito.domain.replace(/^https?:\/\//, ''),
              scopes: config.cognito.scope,
              redirectSignIn: [config.cognito.redirectSignIn],
              redirectSignOut: [config.cognito.redirectSignOut],
              responseType: 'code' as const,
            },
          },
        },
      },
    });

    setAmplifyConfigured(true);
  }, [config]);

  /** --------------------------------------------------
   * Force login on init (Hosted UI) & Fetch token
   * Only runs after Amplify is configured from config.json
   * -------------------------------------------------- */
  useEffect(() => {
    if (!amplifyConfigured) return;

    let isMounted = true;

    const initAuth = async () => {
      try {
        console.log('Starting auth initialization...');
        // fetchAuthSession automatically handles OAuth callback
        const session = await fetchAuthSession({ forceRefresh: false });
        const idToken = session.tokens?.idToken?.toString();

        console.log('Session fetched, has token:', !!idToken);

        if (!isMounted) return;

        if (idToken) {
          console.log('Setting token and rendering app');
          tokenRef.current = idToken;
          setToken(idToken);
          setIsInitialized(true);
        } else {
          // No token available, redirect to login
          console.log('No token found, redirecting to login...');
          await signInWithRedirect();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (!isMounted) return;

        // Don't redirect if we're in OAuth callback
        if (!window.location.search.includes('code=')) {
          console.log('Not in OAuth callback, redirecting to login...');
          await signInWithRedirect();
        } else {
          console.warn(
            'OAuth callback error detected, setting initialized anyway',
          );
          setIsInitialized(true);
        }
      }
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, [amplifyConfigured]);

  /** --------------------------------------------------
   * Refresh token before expiration
   * -------------------------------------------------- */
  useEffect(() => {
    if (!amplifyConfigured || !token) return;

    let timeout: number;

    const setupRefresh = async () => {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;

      if (!idToken) return;

      const payload = idToken.payload;
      const exp = payload.exp;
      if (!exp) {
        logger.warn('ID token expiration missing');
        return;
      }

      const expirationTime = exp * 1000;
      const delay = expirationTime - Date.now() - 10_000;

      timeout = window.setTimeout(async () => {
        logger.info('Refreshing id token...');
        const refreshed = await fetchAuthSession({ forceRefresh: true });
        const newToken = refreshed.tokens?.idToken?.toString();

        if (!newToken) return;

        tokenRef.current = newToken;
        setToken(newToken);
      }, Math.max(delay, 0));
    };

    const call = makeCancelable(setupRefresh());

    return () => {
      clearTimeout(timeout);
      call.cancel();
    };
  }, [amplifyConfigured, token, logger]);

  const value = useMemo(
    () => ({
      getToken: () => tokenRef.current,
    }),
    [],
  );

  // Show loading while config loads, Amplify configures, or auth initializes
  if (!config || !amplifyConfigured || !isInitialized) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        Loading...
      </div>
    );
  }

  // Only render children if we have a token
  if (!token) {
    return null;
  }

  return (
    <AuthTokenContext.Provider value={value}>
      {children}
    </AuthTokenContext.Provider>
  );
};

export interface AuthTokenContextValue {
  getToken: () => string;
}
